import { ChevronDown, ChevronUp } from '@carbon/react/icons';
import {
  ColumnDef,
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Button,
  RadioButton,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { useAtom } from 'jotai';
import { debounce } from 'lodash-es';
import plur from 'plur';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { EmptyState } from 'components/empty-state';
import { PlainLink } from 'components/link';
import { SkeletonTableRows } from 'components/skeleton-table-rows';
import { PaginatedTable } from 'components/table/paginated-table';
import { format } from 'lib/ui';
import type {
  EntityWithAccountsResponse,
  SearchResult,
} from 'types/api-responses';
import type { SearchParams } from 'atoms/search-atom';

import styles from './entity-search-table.module.scss';

const DEFAULT_PAGE_SIZE = 16;

// Stable empty-results reference. Passing `useReactTable({ data })` a fresh `[]`
// on every render makes TanStack's autoResetExpanded reset `expanded` each render
// (→ setExpanded → re-render → new `[]` → …), an infinite loop under React 18.
const EMPTY_RESULTS: EntityWithAccountsResponse[] = [];

/** Account shape from EntityWithAccountsResponse */
type CRMAccount = EntityWithAccountsResponse['accounts'][number];

/** Sub-row type for expanded multi-account entities */
type EntitySubRow = EntityWithAccountsResponse & {
  isSubRow: boolean;
  parentEntity: EntityWithAccountsResponse;
};

/** Search params for entity search API */
export interface EntitySearchParams extends SearchParams {
  text?: string;
  includeAccounts: boolean;
  linkedOnly?: boolean;
}

/** Shape returned by searchAtomPair's read atom */
interface SearchAtomValue {
  loading?: boolean;
  data?: SearchResult<EntityWithAccountsResponse>;
  currentPayload?: EntitySearchParams;
  error?: string;
}

/** The search atom pair returned by searchAtomPair */
interface EntitySearchAtomPair {
  searchAtom: import('jotai').WritableAtom<
    SearchAtomValue,
    [
      | ((
          params: EntitySearchParams | undefined
        ) => EntitySearchParams | undefined)
      | undefined,
    ],
    undefined
  >;
  resetSearchDataAtom: import('jotai').WritableAtom<
    null,
    [{ unsetCurrentPayload: boolean } | undefined],
    undefined
  >;
}

export interface EntitySearchTableProps {
  /** The searchAtomPair instance to use for entity search */
  searchAtom: EntitySearchAtomPair;
  /** Called when entity radio selection changes */
  onEntitySelect?: (entity: EntityWithAccountsResponse | null) => void;
  /** Called when account radio selection changes (entity has multiple accounts) */
  onAccountSelect?: (
    entity: EntityWithAccountsResponse,
    accountIndex: number
  ) => void;
  /** Render prop for action buttons shown above table when results exist */
  renderActions?: (
    selectedEntity: EntityWithAccountsResponse | null
  ) => ReactNode;
  /** Minimum search characters before triggering API call */
  minSearchLength?: number;
  /** Default page size */
  defaultPageSize?: number;
  /** Additional columns appended after the default columns */
  extraColumns?: ColumnDef<EntityWithAccountsResponse, unknown>[];
  /** Params every new search starts with (e.g. linkedOnly), so the first request carries them */
  baseParams?: Partial<EntitySearchParams>;
  /** Keep the page in the URL's `?page=` (default). Off for a picker inside another page. */
  persistentPagination?: boolean;
}

/**
 * Shared entity search table with search input, debounced API search,
 * results table with radio selection, expandable account sub-rows,
 * pagination, and match count.
 * @param {EntitySearchTableProps} props - Search atom, callbacks, and render props
 * @return {JSX.Element} Entity search table
 */
const EntitySearchTable: FC<EntitySearchTableProps> = ({
  searchAtom: searchAtomPair,
  onEntitySelect,
  onAccountSelect,
  renderActions,
  minSearchLength = 3,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  extraColumns,
  baseParams,
  persistentPagination = true,
}) => {
  const { searchAtom } = searchAtomPair;
  const [searchResults, setSearchPayload] = useAtom(searchAtom);
  const searchParams = searchResults.currentPayload;
  const pageSize = searchResults?.data?.pageSize || defaultPageSize;

  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [selectedEntity, setSelectedEntity] = useState<number | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<{
    entityId: number;
    accountIndex: number;
  } | null>(null);
  // Read when a search starts, so a change of params does not rebuild the debounced search.
  const baseParamsRef = useRef(baseParams);
  baseParamsRef.current = baseParams;
  const [searchDisplayText, setSearchDisplayText] = useState(
    searchResults.currentPayload?.text || ''
  );

  // Resolve selected entity data for renderActions
  const selectedEntityData = useMemo(() => {
    if (!selectedEntity) return null;
    return (
      searchResults.data?.results.find(
        (e: EntityWithAccountsResponse) => e.id === selectedEntity
      ) || null
    );
  }, [selectedEntity, searchResults.data?.results]);

  // Debounced search
  const debouncedSetSearchPayload = useMemo(
    () =>
      debounce((value: string) => {
        setSelectedEntity(null);
        setSelectedAccount(null);
        setExpanded({});

        if (value.length >= minSearchLength) {
          setSearchPayload((current: EntitySearchParams | undefined) => ({
            ...baseParamsRef.current,
            ...current,
            pageSize: current?.pageSize || defaultPageSize,
            text: value,
            page: 1,
            includeAccounts: true,
          }));
        } else {
          setSearchPayload(undefined);
        }
      }, 400),
    [setSearchPayload, minSearchLength, defaultPageSize]
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchPayload.cancel();
    };
  }, [debouncedSetSearchPayload]);

  // Trigger initial search on mount when minSearchLength is 0
  useEffect(() => {
    if (
      minSearchLength === 0 &&
      !searchResults.data &&
      !searchResults.loading
    ) {
      setSearchPayload((current: EntitySearchParams | undefined) => ({
        ...baseParamsRef.current,
        ...current,
        pageSize: current?.pageSize || defaultPageSize,
        text: '',
        page: 1,
        includeAccounts: true,
      }));
    }
    // searchResults.data/loading intentionally excluded — including them would cause
    // an infinite loop (effect fires → results arrive → effect re-fires).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minSearchLength]);

  // Notify parent of entity selection
  const handleEntitySelect = useCallback(
    (entity: EntityWithAccountsResponse | null) => {
      setSelectedEntity(entity?.id ?? null);
      if (entity?.accounts && entity.accounts.length > 0) {
        setSelectedAccount({ entityId: entity.id, accountIndex: 0 });
        onAccountSelect?.(entity, 0);
      } else {
        setSelectedAccount(null);
      }
      onEntitySelect?.(entity ?? null);
    },
    [onEntitySelect, onAccountSelect]
  );

  // Handle account sub-row radio selection
  const handleAccountSelect = useCallback(
    (parentEntity: EntityWithAccountsResponse, accountIndex: number) => {
      setSelectedEntity(parentEntity.id);
      setSelectedAccount({ entityId: parentEntity.id, accountIndex });
      onEntitySelect?.(parentEntity);
      onAccountSelect?.(parentEntity, accountIndex);
    },
    [onEntitySelect, onAccountSelect]
  );

  const matchCount = searchResults.data
    ? `${format.number(searchResults.data.totalCount || 0)} ${plur(
        'Match',
        searchResults.data.totalCount
      )}`
    : null;

  // Check if all expandable rows are currently expanded
  const isAllExpanded = useMemo(() => {
    const expandableRowCount =
      searchResults.data?.results.filter(
        (row: EntityWithAccountsResponse) =>
          row.accounts && row.accounts.length > 1
      ).length || 0;
    if (expandableRowCount === 0) return false;

    const expandedRowCount = Object.values(expanded).filter(Boolean).length;
    return expandedRowCount === expandableRowCount;
  }, [searchResults.data?.results, expanded]);

  const handleExpandAll = useCallback(() => {
    if (isAllExpanded) {
      setExpanded({});
    } else {
      const expandedState: ExpandedState = {};
      searchResults.data?.results.forEach(
        (row: EntityWithAccountsResponse, index: number) => {
          if (row.accounts && row.accounts.length > 1) {
            expandedState[index.toString()] = true;
          }
        }
      );
      setExpanded(expandedState);
    }
  }, [isAllExpanded, searchResults.data?.results]);

  const columnHelper = createColumnHelper<EntityWithAccountsResponse>();

  const showRadio = !!onEntitySelect;

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'expander',
        header: () => (
          <Button
            kind="ghost"
            size="sm"
            className={styles.expander}
            aria-label={isAllExpanded ? 'Collapse all' : 'Expand all'}
            aria-expanded={isAllExpanded}
            onClick={handleExpandAll}
            renderIcon={isAllExpanded ? ChevronUp : ChevronDown}
            iconDescription={isAllExpanded ? 'Collapse all' : 'Expand all'}
          />
        ),
        cell: ({ row }) => {
          const hasMultipleAccounts =
            row.original.accounts && row.original.accounts.length > 1;
          const hasAccounts =
            row.original.accounts && row.original.accounts.length > 0;

          if (hasMultipleAccounts && row.getCanExpand()) {
            return (
              <Button
                kind="ghost"
                size="sm"
                className={styles.expander}
                aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
                aria-expanded={row.getIsExpanded()}
                onClick={row.getToggleExpandedHandler()}
                renderIcon={row.getIsExpanded() ? ChevronUp : ChevronDown}
                iconDescription={
                  row.getIsExpanded() ? 'Collapse row' : 'Expand row'
                }
              />
            );
          } else if (hasAccounts) {
            return (
              <Button
                kind="ghost"
                size="sm"
                className={styles.expander}
                disabled
                aria-label="No expansion available"
                renderIcon={ChevronDown}
                iconDescription="No expansion available"
              />
            );
          } else {
            return (
              <Button
                kind="ghost"
                size="sm"
                className={`${styles.expander} ${styles.expanderDisabled}`}
                disabled
                aria-label="No accounts available"
                renderIcon={ChevronDown}
                iconDescription="No accounts available"
              />
            );
          }
        },
      }),
      ...(showRadio
        ? [
            columnHelper.display({
              id: 'radio',
              header: () => null,
              cell: ({
                row,
              }: {
                row: { original: EntityWithAccountsResponse; index: number };
              }) => (
                <RadioButton
                  id={`entity-${row.original.id}`}
                  name="entity-selection"
                  labelText=""
                  checked={selectedEntity === row.original.id}
                  data-testid={`radio-entity-${row.original.id}`}
                  onChange={() => {
                    handleEntitySelect(row.original);

                    // Expand row if it has multiple accounts
                    if (
                      row.original.accounts &&
                      row.original.accounts.length > 1
                    ) {
                      setExpanded((prev) => {
                        const newExpanded = Object.assign({}, prev) as Record<
                          string,
                          boolean
                        >;
                        newExpanded[row.index.toString()] = true;
                        return newExpanded;
                      });
                    }
                  }}
                />
              ),
            }),
          ]
        : []),
      columnHelper.accessor('id', {
        header: 'Entity ID',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('name', {
        header: 'Insights Entity',
        cell: (info) => {
          const entity = info.row.original;
          return (
            <div className={styles.entityInfo}>
              <div className={styles.entityName}>
                <PlainLink
                  href={entity.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {entity.name}
                </PlainLink>
              </div>
              <div className={styles.entityAddress}>
                <span className={styles.label}>Address:</span>
                <span className={styles.value}> {entity.address || '--'}</span>
              </div>
              <div className={styles.entityType}>
                <span className={styles.label}>Entity Type:</span>
                <span className={styles.value}>
                  {' '}
                  {entity.importType || '--'}
                </span>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor('accounts', {
        header: 'Linked CRM Account(s)',
        cell: (info) => {
          const accounts = info.getValue();
          if (!accounts || accounts.length === 0) {
            return <span className={styles.value}>Not Linked</span>;
          }
          if (accounts.length === 1) {
            return (
              <div className={styles.accountsInfo}>
                <div className={styles.accountItem}>
                  <div className={styles.accountName}>
                    <PlainLink
                      href={accounts[0].salesforceHref}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {accounts[0].name}
                    </PlainLink>
                  </div>
                  <div className={styles.accountType}>
                    <span className={styles.label}>Type:</span>
                    <span className={styles.value}>
                      {' '}
                      {accounts[0].type || '--'}
                    </span>
                  </div>
                  <div className={styles.accountAddress}>
                    <span className={styles.label}>Address:</span>
                    <span className={styles.value}>
                      {' '}
                      {accounts[0].billingAddress || '--'}
                    </span>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <span className={styles.value}>{accounts.length} CRM Accounts</span>
          );
        },
      }),
      ...(extraColumns ?? []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      columnHelper,
      isAllExpanded,
      handleExpandAll,
      selectedEntity,
      showRadio,
      extraColumns,
    ]
  );

  const table = useReactTable({
    data: searchResults.data?.results ?? EMPTY_RESULTS,
    columns,
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => {
      if (!row.accounts || row.accounts.length <= 1) {
        return [];
      }

      return row.accounts.map(
        (account) =>
          ({
            id: row.id,
            name: account.name,
            address: account.billingAddress || '',
            importType: account.type || '',
            accounts: [],
            auth: row.auth,
            deleted: row.deleted,
            portfolioInvestment: row.portfolioInvestment,
            hasInventory: row.hasInventory,
            href: row.href,
            emailAddresses: row.emailAddresses,
            phoneNumbers: row.phoneNumbers,
            addresses: row.addresses,
            canonicalId: row.canonicalId,
            resources: row.resources,
            salesforceId: account.salesforceId,
            type: account.type,
            billingAddress: account.billingAddress,
            phone: account.phone,
            isSubRow: true,
            parentEntity: row,
          }) as EntitySubRow
      );
    },
  });

  // Table headers
  const tableHeader = (
    <TableHead>
      {table.getHeaderGroups().map((hg) => (
        <TableRow key={hg.id}>
          {hg.headers.map((header) => (
            <TableHeader key={header.id}>
              {header.isPlaceholder
                ? null
                : flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
            </TableHeader>
          ))}
        </TableRow>
      ))}
    </TableHead>
  );

  // Table body with expandable sub-rows
  const tableBody = (
    <TableBody>
      {table.getRowModel().rows.map((row, rowIndex) => {
        const isSubRow =
          'isSubRow' in row.original && Boolean(row.original.isSubRow);
        const isFirstSubRow =
          isSubRow &&
          rowIndex > 0 &&
          !('isSubRow' in table.getRowModel().rows[rowIndex - 1].original);

        if (isSubRow) {
          const parentEntityId = (row.original as EntitySubRow).parentEntity.id;
          return (
            <React.Fragment key={`subrow-${parentEntityId}-${rowIndex}`}>
              {isFirstSubRow && (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <div className={styles.subTableWrapper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            {showRadio && (
                              <TableHeader
                                className={styles.subTableHeaderCell}
                              ></TableHeader>
                            )}
                            <TableHeader className={styles.subTableHeaderCell}>
                              CRM Account
                            </TableHeader>
                            <TableHeader className={styles.subTableHeaderCell}>
                              Type
                            </TableHeader>
                            <TableHeader className={styles.subTableHeaderCell}>
                              Address
                            </TableHeader>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(
                            row.original as EntitySubRow
                          ).parentEntity.accounts?.map(
                            (account: CRMAccount, index: number) => (
                              <TableRow key={index}>
                                {showRadio && (
                                  <TableCell>
                                    <RadioButton
                                      id={`account-${
                                        (row.original as EntitySubRow)
                                          .parentEntity.id
                                      }-${index}`}
                                      name={`crm-account-${
                                        (row.original as EntitySubRow)
                                          .parentEntity.id
                                      }`}
                                      labelText=""
                                      data-testid={`radio-account-${
                                        (row.original as EntitySubRow)
                                          .parentEntity.id
                                      }-${index}`}
                                      checked={
                                        selectedAccount?.entityId ===
                                          (row.original as EntitySubRow)
                                            .parentEntity.id &&
                                        selectedAccount?.accountIndex === index
                                      }
                                      onChange={() => {
                                        const parent = (
                                          row.original as EntitySubRow
                                        ).parentEntity;
                                        handleAccountSelect(parent, index);

                                        // Preserve expanded state
                                        const parentRow = table
                                          .getRowModel()
                                          .rows.find(
                                            (r) =>
                                              !(
                                                'isSubRow' in r.original &&
                                                r.original.isSubRow
                                              ) && r.original.id === parent.id
                                          );
                                        if (parentRow) {
                                          setExpanded((prev) => {
                                            const newExpanded = Object.assign(
                                              {},
                                              prev
                                            ) as Record<string, boolean>;
                                            newExpanded[
                                              parentRow.index.toString()
                                            ] = true;
                                            return newExpanded;
                                          });
                                        }
                                      }}
                                    />
                                  </TableCell>
                                )}
                                <TableCell>
                                  <div className={styles.accountName}>
                                    {account.salesforceHref ? (
                                      <PlainLink
                                        href={account.salesforceHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {account.name}
                                      </PlainLink>
                                    ) : (
                                      account.name
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>{account.type || '--'}</TableCell>
                                <TableCell>
                                  {account.billingAddress || '--'}
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        }

        return (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id} style={{ paddingLeft: row.depth * 16 }}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        );
      })}
    </TableBody>
  );

  return (
    <div>
      <div className={styles.searchInputContainer}>
        <Search
          labelText="Search"
          placeholder={`Search entities (minimum ${minSearchLength} characters)`}
          size="lg"
          {...{ autoFocus: true }}
          value={searchDisplayText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchDisplayText(e.target.value);
            debouncedSetSearchPayload(e.target.value);
          }}
        />
      </div>
      {!!(
        searchResults.data?.results && searchResults.data.results.length > 0
      ) &&
        searchDisplayText.length >= minSearchLength &&
        !searchResults.loading && (
          <>
            {renderActions && (
              <div className={styles.actionsRow}>
                {renderActions(selectedEntityData)}
              </div>
            )}
          </>
        )}
      {searchDisplayText.length >= minSearchLength &&
        !searchResults.loading &&
        matchCount && <div className={styles.matchCount}>{matchCount}</div>}
      {searchDisplayText.length >= minSearchLength &&
        (searchResults.data || searchResults.loading) && (
          <PaginatedTable
            pageSizes={[defaultPageSize, 24, 48, 64]}
            pageSize={pageSize}
            className={styles.entitiesTable}
            page={searchParams?.page || 1}
            totalItems={searchResults?.data?.totalCount || 0}
            persistentPagination={persistentPagination}
            paginationOnTop={true}
            paginatorOutsideTableContainer={true}
            onPaginate={({
              page,
              pageSize: newPageSize,
            }: {
              page: number;
              pageSize: number;
            }) => {
              setSearchPayload(
                (current: EntitySearchParams | undefined) =>
                  current && {
                    ...current,
                    page,
                    pageSize: newPageSize,
                  }
              );
            }}
            isLoading={searchResults.loading || false}
            loadingComponent={
              <Table>
                {tableHeader}
                <TableBody>
                  <SkeletonTableRows rows={pageSize} columns={columns.length} />
                </TableBody>
              </Table>
            }
            emptyStateComponent={
              <div className={styles.emptyStateContainer}>
                <EmptyState
                  headerText="No entities found"
                  helperText="Try adjusting your search criteria"
                />
              </div>
            }
          >
            {tableHeader}
            {tableBody}
          </PaginatedTable>
        )}
    </div>
  );
};

export { EntitySearchTable };
